import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AREAS, OTHER_AREAS, PROJECT_TYPES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-salmon focus:outline-none focus:ring-2 focus:ring-salmon/20";

const EstimateForm = ({ idPrefix, title, subtitle, full = false, source, defaultProject = "" }) => {
  const empty = { name: "", phone: "", email: "", project_type: defaultProject, city: "", message: "" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, source });
      toast.success("Estimate request received!", {
        description: "We'll call you within one business day with your free quote.",
      });
      setForm(empty);
    } catch {
      toast.error("Something went wrong", {
        description: "Please try again, or call us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${idPrefix}-form`}
      className="rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
    >
      <h2 className="font-serif text-2xl font-bold text-navy">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}

      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor={`${idPrefix}-name`} className="sr-only">Full name</label>
          <input
            id={`${idPrefix}-name`}
            data-testid={`${idPrefix}-name-input`}
            className={inputClass}
            placeholder="Full name"
            required
            value={form.name}
            onChange={set("name")}
          />
        </div>
        <div className={`grid gap-4 ${full ? "sm:grid-cols-2" : ""}`}>
          <div>
            <label htmlFor={`${idPrefix}-phone`} className="sr-only">Phone</label>
            <input
              id={`${idPrefix}-phone`}
              data-testid={`${idPrefix}-phone-input`}
              className={inputClass}
              placeholder="Phone number"
              type="tel"
              required
              value={form.phone}
              onChange={set("phone")}
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-email`} className="sr-only">Email</label>
            <input
              id={`${idPrefix}-email`}
              data-testid={`${idPrefix}-email-input`}
              className={inputClass}
              placeholder="Email address"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
            />
          </div>
        </div>
        <div className={`grid gap-4 ${full ? "sm:grid-cols-2" : ""}`}>
          <div>
            <label htmlFor={`${idPrefix}-project`} className="sr-only">Project type</label>
            <select
              id={`${idPrefix}-project`}
              data-testid={`${idPrefix}-project-select`}
              className={inputClass}
              required
              value={form.project_type}
              onChange={set("project_type")}
            >
              <option value="" disabled>What are we remodeling?</option>
              {PROJECT_TYPES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          {full && (
            <div>
              <label htmlFor={`${idPrefix}-city`} className="sr-only">City</label>
              <select
                id={`${idPrefix}-city`}
                data-testid={`${idPrefix}-city-select`}
                className={inputClass}
                value={form.city}
                onChange={set("city")}
              >
                <option value="">Your city</option>
                {[...AREAS.map((a) => a.name), ...OTHER_AREAS].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {full && (
          <div>
            <label htmlFor={`${idPrefix}-message`} className="sr-only">Project details</label>
            <textarea
              id={`${idPrefix}-message`}
              data-testid={`${idPrefix}-message-input`}
              className={`${inputClass} min-h-[110px] resize-y`}
              placeholder="Tell us about your project (optional)"
              value={form.message}
              onChange={set("message")}
            />
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid={`${idPrefix}-submit-btn`}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-salmon py-3.5 text-sm font-bold text-white shadow-md transition-all hover:bg-salmon-dark hover:shadow-lg disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? "Sending..." : "Get My Free Estimate"}
        </button>
        <p className="text-center text-xs text-slate-400">
          No obligation. No spam. We call you — never the other way around.
        </p>
      </div>
    </form>
  );
};

export default EstimateForm;
